import { gql } from '@apollo/client'
import { FIELD_FRAGMENT } from 'graphql/field/queries'

// fragments
export const PLAN_ACTION_DETAILS_FRAGMENT = gql`
  fragment PLAN_ACTION_DETAILS on PlanAction {
    id
    color
    archived
    milestone
    description
    commencementAt
    completionAt
    completionPercentage
    treePosition
    isBookmark
    ownedById
    ownedByUser {
      id
      firstName
      lastName
    }
    ownedByPosition {
      id
      description
      user {
        id
        firstName
        language
      }
    }
    memberUsers {
      id
      firstName
      lastName
    }
    memberPositions {
      id
      description
    }
    businessUnits {
      id
      description
    }
    businessProcesses {
      id
      description
    }
    businessActivities {
      id
      description
    }
    planItems {
      data {
        id
        companyId
        planId
        path
        plan {
          id
          companyId
          ownedById
          locked
          memberPositions {
            id
          }
          memberUsers {
            id
          }
          private
          parentPlanId
          name
          scheduleDefaults {
            entityType
            levelId
            repeating
            repeatEveryValue
            repeatEveryType
            periodEndsOn
            startPeriod
            endPeriod
            endsOn
            endsOnDate
            reviewDate {
              date
              end
              value
              period
            }
          }
          planType {
            id
            description
            levels {
              id
              color
              position
              description
            }
            objectiveOwnerObjectSecurityId
            objectiveMemberObjectSecurityId
            actionOwnerObjectSecurityId
            actionMemberObjectSecurityId
            kpiOwnerObjectSecurityId
            kpiMemberObjectSecurityId
            planOwnerObjectSecurityId
            planMemberObjectSecurityId
          }
        }
        referenced
      }
    }
    fields {
      ...FIELD
    }
    fieldValueMap {
      fieldName
      fieldValue
      fieldType
    }
  }
  ${FIELD_FRAGMENT}
`

export const PLAN_ACTION_LINKED_ITEMS_FRAGMENT = gql`
  fragment PLAN_ACTION_LINKED_ITEMS on PlanAction {
    destinationLinkingItems {
      id
      parentId
      weighting
      createdAt
      description
      sourcePlanKPIId
      destinationPlanKPIId
      sourcePlanActionId
      destinationPlanActionId
      sourcePlanObjectiveId
      destinationPlanObjectiveId
      sourcePlanKPI {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanKPI {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      sourcePlanAction {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanAction {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      sourcePlanObjective {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanObjective {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
    }
    sourceLinkingItems {
      id
      parentId
      weighting
      createdAt
      description
      sourcePlanKPIId
      destinationPlanKPIId
      sourcePlanActionId
      destinationPlanActionId
      sourcePlanObjectiveId
      destinationPlanObjectiveId
      sourcePlanKPI {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanKPI {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      sourcePlanAction {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanAction {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      sourcePlanObjective {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
      destinationPlanObjective {
        id
        description
        destinationLinkingItems {
          id
        }
        sourceLinkingItems {
          id
        }
        planItems {
          data {
            id
            path
            plan {
              id
              name
            }
            referenced
          }
        }
      }
    }
  }
`

export const PLAN_ACTION_REVIEWS_FRAGMENT = gql`
  fragment PLAN_ACTION_REVIEWS on PlanAction {
    scheduledReviews {
      id
      planId
      companyId
      repeating
      repeatEveryValue
      repeatEveryType
      periodEndsOn
      startPeriod
      endPeriod
      scheduledReviewers {
        id
        userId
        positionId
        scheduledReviewId
        reviewDate {
          end
          value
          date
          period
        }
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        position {
          id
          description
        }
      }
      endsOn
      endsOnDate
    }
    reviews {
      id
      planId
      createdAt
      updatedAt
      startPeriod
      endPeriodAt
      dueAt
      scheduledReviewId
      scheduledReview {
        id
        planId
        companyId
        repeating
        repeatEveryValue
        repeatEveryType
        periodEndsOn
        startPeriod
        endPeriod
        scheduledReviewers {
          id
          userId
          positionId
          scheduledReviewId
          reviewDate {
            end
            value
            date
            period
          }
          user {
            id
            firstName
            lastName
            avatarUrl
          }
          position {
            id
            description
          }
        }
        endsOn
        endsOnDate
      }
      status
      values {
        goals
        doWell
        doBetter
      }
      fieldValueMap {
        fieldId
        fieldValueId
        fieldValue
        fieldLabel
      }
      updatedBy {
        id
        firstName
        lastName
      }
      approvers {
        userId
        positionId
      }
      approvedAt
      approvedBy {
        id
        firstName
        lastName
      }
    }
  }
`

export const PLAN_ACTION_PERMISSIONS_FRAGMENT = gql`
  fragment PLAN_ACTION_PERMISSIONS on PlanAction {
    currentPermissions {
      actionArchiveAction
      actionAddAction
      actionAddKPI
      actionAddObjective
      actionCreateResource
      actionCreateScheduledReview
      actionDeleteAction
      actionDeleteResource
      actionDeleteScheduledReview
      actionLinkObjects
      actionModifyAction
      actionModifyResource
      actionModifyScheduledReview
      actionSetUserActionSecurity
      actionViewCompletedReview
      actionViewInProgressReview
      planLinkObjects
    }
  }
`

// queries
export const PLAN_ACTION_DETAILS_QUERY = gql`
  query PLAN_ACTION_DETAILS_QUERY($filter: PlanActionFilterInput!) {
    planActions(filter: $filter) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        ...PLAN_ACTION_DETAILS
        ...PLAN_ACTION_LINKED_ITEMS
        ...PLAN_ACTION_REVIEWS
        ...PLAN_ACTION_PERMISSIONS
      }
    }
  }
  ${PLAN_ACTION_DETAILS_FRAGMENT}
  ${PLAN_ACTION_LINKED_ITEMS_FRAGMENT}
  ${PLAN_ACTION_REVIEWS_FRAGMENT}
  ${PLAN_ACTION_PERMISSIONS_FRAGMENT}
`

export const PLAN_ACTION_ACTIVITIES_LIST_QUERY = gql`
  query PLAN_ACTION_ACTIVITIES_LIST_QUERY($filter: LogFilterInput!) {
    logs(filter: $filter) {
      data {
        id
        entityType
        entityId
        actionType
        createdAt
        metadata {
          fileName
          fileUrl
          fileContentType
          comment
          resource
        }
        user {
          id
          firstName
          lastName
          avatarUrl
        }
        action {
          id
          description
        }
      }
    }
  }
`

export const PLAN_ACTION_RESOURCES_LIST_QUERY = gql`
  query PLAN_ACTION_RESOURCES_LIST_QUERY($filter: PlanResourcesFilterInput!) {
    planResources(filter: $filter) {
      data {
        id
        planObjectiveId
        planActionId
        createdBy {
          id
          firstName
          lastName
          avatarUrl
        }
        createdAt
        updatedAt
        fieldValueMap {
          fieldName
          fieldValue
          fieldType
          wysiwyg
        }
      }
    }
  }
`

export const PLAN_ACTION_LIST_QUERY = gql`
  query PLAN_ACTION_LIST_QUERY($filter: PlanActionFilterInput!, $order: OrderInput, $pagination: PaginationInput) {
    planActions(filter: $filter, order: $order, pagination: $pagination) {
      pagination {
        pageSkip
        pageTake
        totalCount
      }
      data {
        id
        description
        planItems(filter: { referenced: false }) {
          data {
            id
            plan {
              id
              name
              companyId
            }
          }
        }
        ownedByUser {
          id
          firstName
          lastName
        }
        ownedByPosition {
          id
          description
          user {
            id
            firstName
            language
          }
        }
        completionAt
        completionPercentage
        fieldValueMap {
          fieldName
          fieldValue
        }
        ...PLAN_ACTION_REVIEWS
      }
    }
  }
  ${PLAN_ACTION_REVIEWS_FRAGMENT}
`
